import {
  AnchorHTMLAttributes,
  ClipboardEvent,
  KeyboardEvent,
  PointerEvent,
  ReactNode,
  isValidElement,
  useEffect,
  useRef,
  useState,
} from 'react';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { useSignal } from '@backstage/plugin-signals-react';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import AddIcon from '@material-ui/icons/Add';
import ChatIcon from '@material-ui/icons/Forum';
import CloseIcon from '@material-ui/icons/Close';
import SendIcon from '@material-ui/icons/Send';
import StopIcon from '@material-ui/icons/Stop';
import Markdown from 'markdown-to-jsx';
import { useNavigate } from 'react-router-dom';
import type {
  KnowledgeMessage,
  KnowledgeMessageAttachment,
  KnowledgeCitation,
  KnowledgeSession,
} from '../../api';
import { useKnowledgeChatContext } from './ChatContext';
import { MermaidDiagram } from './MermaidDiagram';
import {
  KnowledgeBuilderColors,
  useKnowledgeBuilderColors,
} from '../../theme/knowledgeBuilderColors';

// TODO export to variable so we can easily change the default suggested prompts from the GlobalAssistantWidget component
type AssistantActivityStatus = 'running' | 'completed' | 'failed';

type AssistantActivity = {
  key: string;
  label: string;
  status: AssistantActivityStatus;
};

type AssistantSignal = {
  type: string;
  sessionId?: string;
  runId?: string;
  toolName?: string;
  actionId?: string;
  label?: string;
  error?: string;
};

const DEFAULT_SUGGESTED_PROMPTS = [
  'Guide me step by step in the creation of the new selected entity',
  'review the architecture and provide feedback',
  'add an ADR',
  'Reference to parent entity documentation and help me write detailed documentation for the selected entity',
];

export const KnowledgeChat = (props: {
  suggestedPrompts?: string[];
  logoUrl?: string;
}) => {
  const suggestedPrompts = props.suggestedPrompts ?? DEFAULT_SUGGESTED_PROMPTS;
  const {
    messages,
    sendMessage,
    abortMessage,
    session,
    uploadFiles,
    resetSession,
    isSending,
  } = useKnowledgeChatContext();
  const colors = useKnowledgeBuilderColors();
  const signalChannel = session?.id
    ? `knowledge.session.${session.id}`
    : 'knowledge.session.unavailable';
  const { lastSignal } = useSignal<AssistantSignal>(signalChannel);
  const identityApi = useApi(identityApiRef);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const attachmentsScrollerRef = useRef<HTMLDivElement | null>(null);
  const [userLabel, setUserLabel] = useState(
    labelFromUserRef(session?.userRef),
  );
  const [value, setValue] = useState('');
  const [localBusy, setBusy] = useState(false);
  const busy = localBusy || isSending;
  const [activities, setActivities] = useState<AssistantActivity[]>([]);
  const [uploading, setUploading] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [attachmentsScroll, setAttachmentsScroll] = useState({
    left: 0,
    max: 0,
    client: 0,
    scrollWidth: 0,
  });
  const effectiveUserLabel =
    userLabel === 'User' ? labelFromUserRef(session?.userRef) : userLabel;

  useEffect(() => {
    let cancelled = false;

    async function loadUserLabel() {
      try {
        const [profile, backstageIdentity] = await Promise.all([
          identityApi.getProfileInfo().catch(() => undefined),
          identityApi.getBackstageIdentity().catch(() => undefined),
        ]);
        const nextLabel =
          profile?.displayName?.trim() ||
          labelFromUserRef(backstageIdentity?.userEntityRef) ||
          labelFromUserRef(session?.userRef) ||
          'User';
        if (!cancelled) {
          setUserLabel(nextLabel);
        }
      } catch {
        if (!cancelled) {
          setUserLabel(labelFromUserRef(session?.userRef));
        }
      }
    }

    loadUserLabel();
    return () => {
      cancelled = true;
    };
  }, [identityApi, session?.userRef]);

  useEffect(() => {
    if (
      !lastSignal ||
      (lastSignal.sessionId && lastSignal.sessionId !== session?.id)
    ) {
      return;
    }

    if (lastSignal.type === 'assistant.started') {
      setActivities([
        { key: 'assistant', label: 'Analyzing request', status: 'running' },
      ]);
      return;
    }

    if (lastSignal.type === 'assistant.tool.started') {
      const key = `tool:${
        lastSignal.toolName ?? lastSignal.actionId ?? Date.now()
      }`;
      setActivities(current => [
        ...current.map(activity =>
          activity.status === 'running'
            ? { ...activity, status: 'completed' as const }
            : activity,
        ),
        {
          key,
          label: lastSignal.label ?? 'Using platform tools',
          status: 'running',
        },
      ]);
      return;
    }

    if (
      lastSignal.type === 'assistant.tool.completed' ||
      lastSignal.type === 'assistant.tool.failed'
    ) {
      const key = `tool:${lastSignal.toolName ?? lastSignal.actionId ?? ''}`;
      setActivities(current => [
        ...current.map(activity => {
          if (activity.key === key) {
            return {
              ...activity,
              status:
                lastSignal.type === 'assistant.tool.completed'
                  ? ('completed' as const)
                  : ('failed' as const),
            };
          }
          return activity.status === 'running'
            ? { ...activity, status: 'completed' as const }
            : activity;
        }),
        {
          key: `thinking:${Date.now()}`,
          label: thinkingLabelAfterTool(lastSignal),
          status: 'running',
        },
      ]);
      return;
    }

    if (lastSignal.type === 'assistant.completed') {
      setActivities(current =>
        current.map(activity =>
          activity.status === 'running'
            ? { ...activity, status: 'completed' as const }
            : activity,
        ),
      );
      return;
    }

    if (lastSignal.type === 'assistant.failed') {
      setActivities(current => [
        ...current.map(activity =>
          activity.status === 'running'
            ? { ...activity, status: 'failed' as const }
            : activity,
        ),
        { key: 'failed', label: 'Assistant failed', status: 'failed' },
      ]);
    }
  }, [lastSignal, session?.id]);

  const lastMessage = messages[messages.length - 1];

  useEffect(() => {
    const messagesContainer = messagesContainerRef.current;
    if (messagesContainer) {
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
    }
  }, [busy, messages.length, lastMessage?.id]);

  useEffect(() => {
    updateAttachmentsScrollState(
      attachmentsScrollerRef.current,
      setAttachmentsScroll,
    );
  }, [attachedFiles.length]);

  const submitMessage = async (overrideValue?: string) => {
    const message = (overrideValue ?? value).trim();
    if (!message && !attachedFiles.length) {
      return;
    }
    if (resetSession && !attachedFiles.length && /^\/clear$/i.test(message)) {
      setValue('');
      await resetSession();
      return;
    }
    const filesToUpload = attachedFiles;
    const attachments: KnowledgeMessageAttachment[] | undefined =
      filesToUpload.length
        ? filesToUpload.map(file => ({
            name: file.name,
            mimeType: file.type || undefined,
            size: file.size,
          }))
        : undefined;
    setValue('');
    setAttachedFiles([]);
    setActivities([]);
    setBusy(true);
    try {
      if (filesToUpload.length) {
        setUploading(true);
        await uploadFiles(filesToUpload);
        setUploading(false);
      }
      await sendMessage(
        message ||
          `Use the attached file${
            filesToUpload.length > 1 ? 's' : ''
          } as context: ${filesToUpload.map(file => file.name).join(', ')}`,
        attachments,
      );
    } finally {
      setUploading(false);
      setBusy(false);
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      event.stopPropagation();
      submitMessage();
    }
  };

  const onAttachFiles = (files: FileList | null) => {
    const selectedFiles = Array.from(files ?? []);
    if (!selectedFiles.length) {
      return;
    }
    setAttachedFiles(current => mergeAttachedFiles(current, selectedFiles));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const onPasteImages = (event: ClipboardEvent<HTMLDivElement>) => {
    const items = event.clipboardData?.items;
    if (!items) {
      return;
    }
    const imageFiles = Array.from(items)
      .filter(item => item.kind === 'file' && item.type.startsWith('image/'))
      .map((item, index) => imageFileFromClipboardItem(item, index))
      .filter((file): file is File => file !== null);
    if (!imageFiles.length) {
      return;
    }
    event.preventDefault();
    setAttachedFiles(current => mergeAttachedFiles(current, imageFiles));
  };

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        maxWidth: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        background: colors.bg.root,
        color: colors.text.primary,
      }}
    >
      <div
        style={{
          flexShrink: 0,
          padding: '10px 10px 8px',
          boxSizing: 'border-box',
          borderBottom: `1px solid ${colors.border.subtle}`,
          background: colors.bg.root,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <div
            style={{
              width: 24,
              height: 24,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {props.logoUrl ? (
              <img
                src={props.logoUrl}
                alt=""
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            ) : (
              <ChatIcon style={{ fontSize: 20, color: colors.text.primary }} />
            )}
          </div>
          <Typography
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: colors.text.primary,
            }}
          >
            R-DAP Assistant
          </Typography>
        </div>
        <Typography
          style={{
            fontSize: 11,
            color: colors.text.muted,
            marginTop: 2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {labelFromFocus(session?.focus)}
        </Typography>
      </div>
      <div
        ref={messagesContainerRef}
        data-testid="knowledge-chat-messages"
        className="knowledge-chat-scrollbar-none"
        style={{
          flex: 1,
          minWidth: 0,
          maxWidth: '100%',
          overflow: 'auto',
          padding: '10px 10px 12px',
          boxSizing: 'border-box',
          background: colors.bg.root,
        }}
      >
        {messages.length === 0 && (
          <div>
            <Typography
              style={{
                fontSize: 11,
                color: colors.text.muted,
                marginBottom: 6,
                textTransform: 'uppercase',
                letterSpacing: 0.4,
              }}
            >
              Try asking
            </Typography>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                gap: 6,
              }}
            >
              {suggestedPrompts.map(prompt => (
                <button
                  key={prompt}
                  type="button"
                  disabled={busy || uploading}
                  onClick={() => submitMessage(prompt)}
                  style={suggestedPromptStyle(colors)}
                >
                  {prompt}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((message, index) => (
          <ChatMessage
            key={message.id}
            message={message}
            userLabel={effectiveUserLabel}
            showDivider={message.role === 'user' && index < messages.length - 1}
          />
        ))}
        {busy && (
          <div
            style={{
              padding: '12px 0',
            }}
          >
            <Typography
              variant="caption"
              style={{ color: colors.text.muted, fontSize: 11 }}
            >
              R-DAP
            </Typography>
            <AgentActivity activities={activities} />
          </div>
        )}
      </div>
      <div
        className="knowledge-builder-chat-composer"
        onPaste={onPasteImages}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: 6,
          margin: '0 10px 10px',
          padding: '8px 8px 6px',
          border: 0,
          borderRadius: 14,
          minWidth: 0,
          maxWidth: '100%',
          boxSizing: 'border-box',
          overflow: 'hidden',
          background: colors.bg.panel,
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          style={{ display: 'none' }}
          onChange={event => onAttachFiles(event.target.files)}
        />
        {attachedFiles.length > 0 && (
          <>
            <div
              ref={attachmentsScrollerRef}
              className="knowledge-builder-scrollbar-none"
              style={{
                display: 'flex',
                flexWrap: 'nowrap',
                gap: 8,
                overflowX: 'auto',
                overflowY: 'hidden',
                minWidth: 0,
                maxWidth: '100%',
                padding: '2px 2px 6px',
                boxSizing: 'border-box',
                scrollbarWidth: 'none',
              }}
              onScroll={event =>
                updateAttachmentsScrollState(
                  event.currentTarget,
                  setAttachmentsScroll,
                )
              }
            >
              {attachedFiles.map(file => (
                <AttachedFilePreview
                  key={`${file.name}-${file.size}-${file.lastModified}`}
                  file={file}
                  onRemove={() =>
                    setAttachedFiles(current =>
                      current.filter(item => item !== file),
                    )
                  }
                />
              ))}
            </div>
            {attachmentsScroll.max > 0 && (
              <AttachmentScrollBar
                scroll={attachmentsScroll}
                onScrollTo={left => {
                  if (attachmentsScrollerRef.current) {
                    attachmentsScrollerRef.current.scrollLeft = left;
                    updateAttachmentsScrollState(
                      attachmentsScrollerRef.current,
                      setAttachmentsScroll,
                    );
                  }
                }}
              />
            )}
          </>
        )}
        <TextField
          value={value}
          onChange={event => setValue(event.target.value)}
          variant="standard"
          size="small"
          fullWidth
          multiline
          maxRows={4}
          placeholder={
            attachedFiles.length
              ? 'Ask for follow-up changes'
              : 'Message the assistant'
          }
          onKeyDown={onKeyDown}
          disabled={busy || uploading}
          style={{ minWidth: 0 }}
          InputProps={{
            disableUnderline: true,
            style: {
              background: 'transparent',
              color: colors.text.primary,
              minHeight: 72,
              border: 0,
              borderRadius: 0,
              alignItems: 'flex-start',
            },
          }}
          inputProps={{
            style: {
              fontSize: 12,
              lineHeight: 1.3,
              padding: '0 2px 4px',
            },
          }}
        />
        <div style={sendRowStyle}>
          <Tooltip title="Add files to context">
            <span>
              <IconButton
                type="button"
                size="small"
                disabled={busy || uploading}
                style={attachButtonStyle(colors)}
                onClick={event => {
                  event.preventDefault();
                  event.stopPropagation();
                  fileInputRef.current?.click();
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={busy ? 'Stop generating' : 'Send'}>
            <span>
              <IconButton
                type="button"
                size="small"
                disabled={!busy && uploading}
                style={sendButtonStyle(colors)}
                onClick={event => {
                  event.preventDefault();
                  event.stopPropagation();
                  if (busy) {
                    abortMessage();
                  } else {
                    submitMessage();
                  }
                }}
              >
                {busy ? (
                  <StopIcon fontSize="small" />
                ) : (
                  <SendIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </div>
      </div>
      <style>
        {`
          .knowledge-chat-scrollbar-none {
            scrollbar-width: none;
          }
          .knowledge-chat-scrollbar-none::-webkit-scrollbar {
            width: 0;
            height: 0;
          }
        `}
      </style>
    </div>
  );
};

function ChatMessage(options: {
  message: KnowledgeMessage;
  userLabel: string;
  showDivider: boolean;
}) {
  const { message, userLabel, showDivider } = options;
  const colors = useKnowledgeBuilderColors();
  if (message.role === 'user') {
    return (
      <div
        style={{
          padding: '10px 0',
          borderBottom: showDivider
            ? `1px solid ${colors.border.default}`
            : undefined,
          color: colors.text.primary,
          minWidth: 0,
          maxWidth: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
        }}
      >
        {message.attachments && message.attachments.length > 0 && (
          <MessageAttachments attachments={message.attachments} />
        )}
        <div
          style={{
            maxWidth: '86%',
            minWidth: 0,
            padding: '10px 12px',
            borderRadius: 12,
            background: colors.chat.userBubbleBg,
            color: colors.chat.userBubbleText,
            boxSizing: 'border-box',
            overflow: 'hidden',
          }}
        >
          <Typography
            variant="caption"
            style={{
              color: colors.accent.blue,
              fontSize: 11,
              display: 'block',
              marginBottom: 6,
            }}
          >
            {labelForRole(message.role, userLabel)}
          </Typography>
          <div
            style={{
              fontSize: 12,
              lineHeight: 1.4,
              minWidth: 0,
              maxWidth: '100%',
              overflowWrap: 'anywhere',
            }}
          >
            <Markdown options={markdownOptions}>{message.content}</Markdown>
          </div>
        </div>
        <MessageMeta message={message} />
      </div>
    );
  }

  return (
    <div
      style={{
        padding: '12px 0',
        color: colors.text.primary,
        minWidth: 0,
        maxWidth: '100%',
        overflow: 'hidden',
      }}
    >
      <Typography
        variant="caption"
        style={{
          color:
            message.role === 'assistant'
              ? colors.accent.blue
              : colors.text.muted,
          fontSize: 11,
          display: 'block',
          marginBottom: 6,
        }}
      >
        {labelForRole(message.role, userLabel)}
      </Typography>
      <div
        style={{
          fontSize: 12,
          lineHeight: 1.4,
          minWidth: 0,
          maxWidth: '100%',
          overflowWrap: 'anywhere',
        }}
      >
        <Markdown options={markdownOptions}>
          {linkedCitationText(message)}
        </Markdown>
      </div>
      {message.role === 'assistant' && message.navigation && (
        <InternalNavigationButton navigation={message.navigation} />
      )}
      {message.role === 'assistant' && <SourceReport message={message} />}
      {message.role === 'assistant' && message.durationMs !== undefined && (
        <Typography
          variant="caption"
          style={{
            display: 'block',
            marginTop: 6,
            color: colors.text.muted,
            fontSize: 11,
          }}
        >
          Responded in {formatDuration(message.durationMs)}
        </Typography>
      )}
      <MessageMeta message={message} />
    </div>
  );
}

function safeSourceUrl(url?: string) {
  return url &&
    (/^https?:\/\//i.test(url) ||
      (url.startsWith('/') && !url.startsWith('//') && !url.includes('\\')))
    ? url
    : undefined;
}

function linkedCitationText(message: KnowledgeMessage) {
  if (message.role !== 'assistant' || !message.citations?.length)
    return message.content;
  const separatedCitations = message.content.replace(
    /(\[\d+\])[ \t]*(?=\[\d+\])/g,
    '$1 · ',
  );
  return separatedCitations.replace(/\[(\d+)\](?!\()/g, (match, number) => {
    const citation = message.citations?.[Number(number) - 1];
    const url = safeSourceUrl(citation?.url);
    return url ? `[${number}](${url.replace(/\)/g, '%29')})` : match;
  });
}

function SourceReport({ message }: { message: KnowledgeMessage }) {
  const colors = useKnowledgeBuilderColors();
  const citations = message.citations ?? [];
  const cited = new Set(citations.map(citation => citation.sourceId));
  const consulted = (message.trace ?? [])
    .flatMap(step => step.sources)
    .filter(source => !cited.has(source.sourceId));
  const otherSources = [
    ...new Map(consulted.map(source => [source.sourceId, source])).values(),
  ];
  const catalogReferences = catalogReferencesFromTrace(message.trace ?? []);
  const textStyle = {
    color: colors.text.secondary,
    fontSize: 11,
    lineHeight: 1.5,
  };
  if (!citations.length && !otherSources.length && !message.trace?.length) {
    return (
      <div style={{ ...textStyle, marginTop: 8 }}>
        Nessuna fonte consultata per questa risposta
      </div>
    );
  }
  return (
    <details style={{ ...textStyle, marginTop: 8 }}>
      <summary style={{ cursor: 'pointer' }}>
        {citations.length
          ? `Fonti citate · ${citations.length}`
          : otherSources.length
          ? `Fonti consultate · ${otherSources.length}`
          : `Ricerche effettuate · ${message.trace?.length ?? 0}`}
      </summary>
      {citations.map((citation, index) => (
        <div
          key={`${citation.sourceId}-${index}`}
          style={{ marginTop: 5, overflowWrap: 'anywhere' }}
        >
          [{index + 1}] {(citation.type ?? 'fonte').toUpperCase()} ·{' '}
          {sourceLink(citation, colors.accent.blue)}
          {citation.location ? ` · ${citation.location}` : ''}
          {citation.revision ? ` · ${citation.revision.slice(0, 12)}` : ''}
          {citation.stepId
            ? citationStepPath(message, citation.stepId, index)
            : ''}
        </div>
      ))}
      {catalogReferences.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <strong>Link esterni registrati nel Catalogo</strong>
          <div>
            Collegamenti disponibili; contenuto non consultato in questa
            risposta.
          </div>
          <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
            {catalogReferences.map(reference => (
              <li key={`${reference.entityRef}:${reference.url}`}>
                {reference.entityRef} ·{' '}
                <a
                  href={reference.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: colors.accent.blue,
                    textDecoration: 'underline',
                  }}
                >
                  {reference.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
      {message.trace?.length ? (
        <details style={{ marginTop: 7 }}>
          <summary style={{ cursor: 'pointer' }}>
            Mostra percorso completo
          </summary>
          {message.trace.map((step, index) => (
            <details
              key={step.id}
              style={{
                marginTop: 8,
                padding: 8,
                border: `1px solid ${colors.border.subtle}`,
                borderRadius: 6,
              }}
            >
              <summary style={{ cursor: 'pointer' }}>
                {index + 1}. {traceToolLabel(step.tool)} ·{' '}
                {new Date(step.at).toLocaleString()}
                {` · ${traceResultSummary(step)}`}
                {step.truncated ? ' · contenuto tagliato' : ''}
              </summary>
              {citations.some(citation => citation.stepId === step.id) && (
                <div style={{ marginTop: 6 }}>
                  Citazioni collegate:{' '}
                  {citations
                    .flatMap((citation, citationIndex) =>
                      citation.stepId === step.id
                        ? [`[${citationIndex + 1}]`]
                        : [],
                    )
                    .join(', ')}
                </div>
              )}
              <div style={{ marginTop: 6 }}>Richiesta</div>
              <pre
                style={{
                  whiteSpace: 'pre-wrap',
                  overflowWrap: 'anywhere',
                  fontSize: 10,
                  margin: '3px 0 8px',
                }}
              >
                {JSON.stringify(step.input, null, 2)}
              </pre>
              {step.sources.length > 0 && (
                <div>
                  <div>Risorse individuate</div>
                  <ul style={{ margin: '3px 0 8px', paddingLeft: 20 }}>
                    {step.sources.map(source => (
                      <li key={source.sourceId}>
                        {sourceLink(source, colors.accent.blue)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {githubCollectionLink(step) && (
                <div style={{ marginBottom: 8 }}>
                  <a
                    href={githubCollectionLink(step)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: colors.accent.blue,
                      textDecoration: 'underline',
                    }}
                  >
                    Apri elenco su GitHub
                  </a>
                </div>
              )}
              <details>
                <summary style={{ cursor: 'pointer' }}>
                  Anteprima del risultato tecnico
                </summary>
                <pre
                  style={{
                    whiteSpace: 'pre-wrap',
                    overflowWrap: 'anywhere',
                    fontSize: 10,
                    maxHeight: 220,
                    overflow: 'auto',
                  }}
                >
                  {traceOutputPreview(step.output)}
                </pre>
                <button
                  type="button"
                  onClick={() => downloadTraceOutput(step.id, step.output)}
                >
                  Scarica risultato originale
                </button>
              </details>
            </details>
          ))}
          {otherSources.length > 0 && (
            <div>Altre risorse consultate · {otherSources.length}</div>
          )}
          {otherSources.map(source => (
            <div key={source.sourceId}>
              {sourceLink(source, colors.accent.blue)}
            </div>
          ))}
          {message.runId && <div>Esecuzione: {message.runId}</div>}
        </details>
      ) : null}
    </details>
  );
}

function citationStepPath(
  message: KnowledgeMessage,
  stepId: string,
  citationIndex: number,
) {
  const stepIndex = message.trace?.findIndex(step => step.id === stepId) ?? -1;
  const step = message.trace?.[stepIndex];
  return step
    ? ` · risposta → [${citationIndex + 1}] → passaggio ${
        stepIndex + 1
      } (${traceToolLabel(step.tool)})`
    : ` · passaggio ${stepId}`;
}

function catalogReferencesFromTrace(
  steps: NonNullable<KnowledgeMessage['trace']>,
) {
  const references = new Map<
    string,
    { entityRef: string; label: string; url: string }
  >();
  const add = (entityRef: string, label: string, rawUrl: unknown) => {
    if (typeof rawUrl !== 'string') return;
    const url = rawUrl.replace(/^url:/, '');
    if (!/^https?:\/\//i.test(url) || !safeSourceUrl(url)) return;
    references.set(`${entityRef}:${url}`, { entityRef, label, url });
  };
  const visit = (value: unknown): void => {
    if (Array.isArray(value)) {
      value.forEach(visit);
      return;
    }
    if (!value || typeof value !== 'object') return;
    const item = value as Record<string, unknown>;
    const metadata =
      item.metadata && typeof item.metadata === 'object'
        ? (item.metadata as Record<string, unknown>)
        : undefined;
    const entityRef =
      typeof item.entityRef === 'string'
        ? item.entityRef
        : typeof item.kind === 'string' && typeof metadata?.name === 'string'
        ? `${item.kind}:${metadata.namespace ?? 'default'}/${
            metadata.name
          }`.toLowerCase()
        : undefined;
    if (entityRef) {
      const annotations = (metadata?.annotations ?? item.annotations) as
        | Record<string, unknown>
        | undefined;
      const slug = annotations?.['github.com/project-slug'];
      if (typeof slug === 'string' && /^[\w.-]+\/[\w.-]+$/.test(slug)) {
        add(
          entityRef,
          `Repository GitHub · ${slug}`,
          `https://github.com/${slug}`,
        );
      }
      add(
        entityRef,
        'Origine nel Catalogo',
        annotations?.['backstage.io/source-location'],
      );
      const links = metadata?.links ?? item.links;
      if (Array.isArray(links)) {
        for (const link of links) {
          if (link && typeof link === 'object') {
            const external = link as Record<string, unknown>;
            add(
              entityRef,
              typeof external.title === 'string'
                ? external.title
                : 'Link esterno',
              external.url,
            );
          }
        }
      }
    }
    for (const key of [
      'result',
      'entity',
      'items',
      'entities',
      'tenants',
      'domains',
    ]) {
      if (item[key]) visit(item[key]);
    }
  };
  for (const step of steps) {
    if (!step.tool.includes('catalog') && !step.tool.includes('tenant'))
      continue;
    try {
      visit(JSON.parse(step.output));
    } catch {
      // A truncated tool result may not contain complete catalog references.
    }
  }
  return [...references.values()];
}

type ChatTraceStep = NonNullable<KnowledgeMessage['trace']>[number];

function traceResultSummary(step: ChatTraceStep) {
  try {
    const result = JSON.parse(step.output)?.result;
    if (result?.accessDenied) return 'accesso negato';
    if (result?.error) return 'errore';
    if (step.tool === 'github_issues_list' && Array.isArray(result?.issues))
      return `${result.issues.length} issue`;
    if (
      step.tool === 'github_pull-requests_list' &&
      Array.isArray(result?.pullRequests)
    )
      return `${result.pullRequests.length} pull request`;
    if (step.tool === 'github_code_search' && Array.isArray(result?.paths))
      return `${result.paths.length} percorsi`;
    for (const [tool, key, label] of [
      ['github_commits_list', 'commits', 'commit'],
      ['github_branches_list', 'branches', 'branch'],
      ['github_tags_list', 'tags', 'tag'],
      ['github_releases_list', 'releases', 'release'],
    ]) {
      if (step.tool === tool && Array.isArray(result?.[key]))
        return `${result[key].length} ${label}${
          result.hasMore ? ' · altri disponibili' : ''
        }`;
    }
  } catch {
    // The recorded output can be truncated.
  }
  return step.sources.length
    ? `${step.sources.length} fonti`
    : 'risultato registrato';
}

function githubCollectionLink(step: ChatTraceStep) {
  const collections: Record<string, string> = {
    github_commits_list: 'commits',
    github_branches_list: 'branches',
    github_tags_list: 'tags',
    github_releases_list: 'releases',
  };
  if (
    !collections[step.tool] &&
    step.tool !== 'github_issues_list' &&
    step.tool !== 'github_pull-requests_list'
  )
    return undefined;
  try {
    const result = JSON.parse(step.output)?.result;
    if (result?.error || !/^[\w.-]+\/[\w.-]+$/.test(result?.repository))
      return undefined;
    if (collections[step.tool])
      return `https://github.com/${result.repository}/${
        collections[step.tool]
      }`;
    const state =
      typeof step.input.state === 'string' ? step.input.state : 'open';
    const issueSearch = step.tool === 'github_issues_list';
    const terms = [
      issueSearch ? 'is:issue' : 'is:pr',
      ...(state === 'all' ? [] : [`is:${state}`]),
      ...(issueSearch && Array.isArray(step.input.labels)
        ? step.input.labels.map(label => `label:${String(label)}`)
        : []),
    ];
    return `https://github.com/${result.repository}/${
      issueSearch ? 'issues' : 'pulls'
    }?q=${encodeURIComponent(terms.join(' '))}`;
  } catch {
    return undefined;
  }
}

function sourceLink(source: KnowledgeCitation, linkColor: string) {
  const href = safeSourceUrl(source.url);
  return href ? (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: linkColor, textDecoration: 'underline' }}
    >
      {source.label}
    </a>
  ) : (
    source.label
  );
}

function traceToolLabel(tool: string) {
  const names: Record<string, string> = {
    'assistant-model-configuration': 'Configurazione del chatbot',
    'catalog_get-catalog-entity': 'Lettura entità Catalogo',
    'catalog_query-catalog-entities': 'Ricerca nel Catalogo',
    'knowledge_read-techdocs': 'Lettura TechDocs',
    github_code_search: 'Ricerca nel codice',
    github_code_read: 'Lettura codice',
    github_issues_list: 'Ricerca issue GitHub',
    github_issues_get: 'Lettura issue GitHub',
    'github_pull-requests_list': 'Ricerca pull request GitHub',
    github_commits_list: 'Cronologia commit GitHub',
    github_branches_list: 'Branch GitHub',
    github_tags_list: 'Tag GitHub',
    github_releases_list: 'Release GitHub',
    'indexed-source-context': 'Fonti indicizzate',
    'session-document-context': 'Documenti della sessione',
    'web_search:search': 'Ricerca web',
  };
  return names[tool] ?? tool.replace(/[_:-]/g, ' ');
}

function traceOutputPreview(output: string) {
  try {
    const compact = (value: unknown): unknown => {
      if (typeof value === 'string' && value.length > 300)
        return `[${value.length} caratteri; scarica il risultato originale]`;
      if (Array.isArray(value)) return value.map(compact);
      if (value && typeof value === 'object') {
        return Object.fromEntries(
          Object.entries(value).map(([key, item]) => [key, compact(item)]),
        );
      }
      return value;
    };
    const pretty = JSON.stringify(compact(JSON.parse(output)), null, 2);
    return pretty.length > 6_000
      ? `${pretty.slice(
          0,
          6_000,
        )}\n… anteprima limitata; scarica il risultato originale`
      : pretty;
  } catch {
    const readable = output
      .replace(
        /[A-Za-z0-9+/=]{300,}/g,
        match => `[${match.length} caratteri omessi nell'anteprima]`,
      )
      .replace(/},\{/g, '},\n{');
    return readable.length > 6_000
      ? `${readable.slice(
          0,
          6_000,
        )}\n… anteprima limitata; scarica il risultato originale`
      : readable;
  }
}

function downloadTraceOutput(id: string, output: string) {
  const blob = new Blob([output], { type: 'application/json;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `backstage-trace-${id.replace(/[^a-zA-Z0-9_-]/g, '-')}.json`;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const INTERNAL_ROUTE_PATTERNS = [
  /^\/$/,
  /^\/catalog(?:\/|$)/,
  /^\/catalog-graph(?:\/|$)/,
  /^\/create(?:\/|$)/,
  /^\/docs(?:\/|$)/,
  /^\/announcements(?:\/|$)/,
  /^\/settings(?:\/|$)/,
  /^\/visualizer\/tree(?:\/|$)/,
  /^\/devtools(?:\/|$)/,
];

function isAllowedInternalNavigation(href: string): boolean {
  if (!href.startsWith('/') || href.startsWith('//') || href.includes('\\')) {
    return false;
  }
  try {
    const target = new URL(href, window.location.origin);
    return (
      target.origin === window.location.origin &&
      INTERNAL_ROUTE_PATTERNS.some(pattern => pattern.test(target.pathname))
    );
  } catch {
    return false;
  }
}

function InternalNavigationButton(options: {
  navigation: { href: string; label: string };
}) {
  const navigate = useNavigate();
  const { navigation } = options;
  if (!isAllowedInternalNavigation(navigation.href)) {
    return null;
  }
  return (
    <Button
      variant="outlined"
      color="primary"
      size="small"
      onClick={() => navigate(navigation.href)}
      style={{ marginTop: 10, textTransform: 'none' }}
    >
      {navigation.label}
    </Button>
  );
}

function AttachedFilePreview(options: { file: File; onRemove(): void }) {
  const { file, onRemove } = options;
  const colors = useKnowledgeBuilderColors();
  return (
    <div
      style={{
        width: 210,
        minWidth: 180,
        maxWidth: '100%',
        padding: '10px 10px',
        borderRadius: 12,
        border: `1px solid ${colors.border.subtle}`,
        background: colors.bg.panel,
        display: 'grid',
        gridTemplateColumns: '34px minmax(0, 1fr) 20px',
        alignItems: 'center',
        gap: 8,
        color: colors.text.primary,
        boxSizing: 'border-box',
        flexShrink: 0,
      }}
    >
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: 7,
          background: fileAccentColor(file),
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 8,
          fontWeight: 800,
          textTransform: 'uppercase',
        }}
      >
        {fileExtension(file)}
      </div>
      <div style={{ minWidth: 0 }}>
        <div
          title={file.name}
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: 12,
            fontWeight: 650,
            color: colors.text.primary,
          }}
        >
          {file.name}
        </div>
        <div
          style={{
            marginTop: 2,
            fontSize: 11,
            color: colors.text.muted,
            textTransform: 'uppercase',
          }}
        >
          {fileExtension(file)}
        </div>
      </div>
      <Tooltip title="Remove">
        <IconButton
          size="small"
          aria-label={`Remove ${file.name}`}
          style={{
            width: 20,
            height: 20,
            padding: 2,
            color: colors.text.muted,
          }}
          onClick={onRemove}
        >
          <CloseIcon style={{ fontSize: 13 }} />
        </IconButton>
      </Tooltip>
    </div>
  );
}

function MessageAttachments(options: {
  attachments: KnowledgeMessageAttachment[];
}) {
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'flex-end',
        gap: 6,
        marginBottom: 6,
        maxWidth: '86%',
      }}
    >
      {options.attachments.map((attachment, index) => (
        <MessageAttachmentChip
          key={`${attachment.name}-${index}`}
          attachment={attachment}
        />
      ))}
    </div>
  );
}

function MessageAttachmentChip(options: {
  attachment: KnowledgeMessageAttachment;
}) {
  const { attachment } = options;
  const colors = useKnowledgeBuilderColors();
  const extension = extensionFromAttachment(attachment);
  return (
    <div
      title={attachment.name}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        maxWidth: 200,
        padding: '5px 8px',
        borderRadius: 8,
        border: `1px solid ${colors.border.subtle}`,
        background: colors.bg.panel,
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          width: 20,
          height: 20,
          flexShrink: 0,
          borderRadius: 5,
          background: accentColorForExtension(extension),
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 7,
          fontWeight: 800,
          textTransform: 'uppercase',
        }}
      >
        {extension}
      </div>
      <span
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontSize: 11,
          color: colors.text.secondary,
        }}
      >
        {attachment.name}
      </span>
    </div>
  );
}

function extensionFromAttachment(attachment: KnowledgeMessageAttachment) {
  const nameExtension = attachment.name.split('.').pop();
  if (nameExtension && nameExtension !== attachment.name) {
    return nameExtension.slice(0, 5).toUpperCase();
  }
  return (
    attachment.mimeType?.split('/').pop()?.slice(0, 5).toUpperCase() || 'FILE'
  );
}

function accentColorForExtension(extension: string) {
  const normalized = extension.toLowerCase();
  if (normalized === 'pdf') {
    return '#ef4444';
  }
  if (['png', 'jpg', 'jpeg', 'webp'].includes(normalized)) {
    return '#3b82f6';
  }
  if (['yaml', 'yml', 'json'].includes(normalized)) {
    return '#22c55e';
  }
  return '#6b7280';
}

function AttachmentScrollBar(options: {
  scroll: { left: number; max: number; client: number; scrollWidth: number };
  onScrollTo(left: number): void;
}) {
  const { scroll, onScrollTo } = options;
  const colors = useKnowledgeBuilderColors();
  const thumbWidth = Math.max(
    18,
    Math.min(100, (scroll.client / scroll.scrollWidth) * 100),
  );
  const thumbLeft = scroll.max
    ? (scroll.left / scroll.max) * (100 - thumbWidth)
    : 0;
  const onTrackPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) {
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = Math.min(
      1,
      Math.max(0, (event.clientX - rect.left) / rect.width),
    );
    onScrollTo(ratio * scroll.max);
  };

  return (
    <div
      role="scrollbar"
      aria-orientation="horizontal"
      aria-valuemin={0}
      aria-valuemax={scroll.max}
      aria-valuenow={scroll.left}
      style={{
        height: 8,
        margin: '-2px 3px 4px',
        borderRadius: 999,
        background: colors.scrollbar.track,
        position: 'relative',
        cursor: 'pointer',
        touchAction: 'none',
      }}
      onPointerDown={onTrackPointerDown}
    >
      <div
        style={{
          position: 'absolute',
          top: 2,
          bottom: 2,
          left: `${thumbLeft}%`,
          width: `${thumbWidth}%`,
          borderRadius: 999,
          background: colors.scrollbar.thumb,
          cursor: 'grab',
          touchAction: 'none',
        }}
        onPointerDown={event => {
          event.preventDefault();
          event.stopPropagation();
          const target = event.currentTarget;
          const track = target.parentElement;
          if (!track) {
            return;
          }
          target.setPointerCapture(event.pointerId);
          const rect = event.currentTarget.getBoundingClientRect();
          const trackRect = track.getBoundingClientRect();
          const grabOffset = event.clientX - rect.left;

          const move = (moveEvent: globalThis.PointerEvent) => {
            const availableWidth = Math.max(1, trackRect.width - rect.width);
            const nextLeft = Math.min(
              availableWidth,
              Math.max(0, moveEvent.clientX - trackRect.left - grabOffset),
            );
            onScrollTo((nextLeft / availableWidth) * scroll.max);
          };

          const stop = (stopEvent: globalThis.PointerEvent) => {
            target.releasePointerCapture(stopEvent.pointerId);
            target.removeEventListener('pointermove', move);
            target.removeEventListener('pointerup', stop);
            target.removeEventListener('pointercancel', stop);
          };

          target.addEventListener('pointermove', move);
          target.addEventListener('pointerup', stop);
          target.addEventListener('pointercancel', stop);
        }}
      />
    </div>
  );
}

function updateAttachmentsScrollState(
  element: HTMLDivElement | null,
  setScroll: (value: {
    left: number;
    max: number;
    client: number;
    scrollWidth: number;
  }) => void,
) {
  if (!element) {
    setScroll({ left: 0, max: 0, client: 0, scrollWidth: 0 });
    return;
  }
  setScroll({
    left: element.scrollLeft,
    max: Math.max(0, element.scrollWidth - element.clientWidth),
    client: element.clientWidth,
    scrollWidth: element.scrollWidth,
  });
}

function imageFileFromClipboardItem(
  item: DataTransferItem,
  index: number,
): File | null {
  const file = item.getAsFile();
  if (!file) {
    return null;
  }
  const extension = item.type.split('/').pop() || 'png';
  const suffix = index > 0 ? `-${index}` : '';
  return new File([file], `pasted-image-${Date.now()}${suffix}.${extension}`, {
    type: item.type,
    lastModified: file.lastModified,
  });
}

function mergeAttachedFiles(current: File[], next: File[]) {
  const filesByKey = new Map(
    current.map(file => [attachedFileKey(file), file] as const),
  );
  for (const file of next) {
    filesByKey.set(attachedFileKey(file), file);
  }
  return Array.from(filesByKey.values());
}

function attachedFileKey(file: File) {
  return `${file.name}:${file.size}:${file.lastModified}`;
}

function fileExtension(file: File) {
  const extension = file.name.split('.').pop();
  if (extension && extension !== file.name) {
    return extension.slice(0, 5).toUpperCase();
  }
  return file.type.split('/').pop()?.slice(0, 5).toUpperCase() || 'FILE';
}

function fileAccentColor(file: File) {
  return accentColorForExtension(fileExtension(file));
}

function MessageMeta(options: { message: KnowledgeMessage }) {
  const { message } = options;
  const colors = useKnowledgeBuilderColors();
  return (
    <div
      style={{
        marginTop: 5,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        color: colors.text.muted,
        fontSize: 11,
      }}
    >
      <span>{formatMessageTime(message.createdAt)}</span>
      <Tooltip title="Copy message">
        <IconButton
          size="small"
          aria-label="Copy message"
          style={{
            padding: 2,
            color: colors.text.secondary,
          }}
          onClick={() => copyMessage(message.content)}
        >
          <FileCopyOutlinedIcon style={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
    </div>
  );
}

function formatMessageTime(createdAt: string) {
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return new Intl.DateTimeFormat(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function formatDuration(durationMs: number) {
  const seconds = durationMs / 1000;
  if (seconds < 10) {
    return `${seconds.toFixed(1)}s`;
  }
  return `${Math.round(seconds)}s`;
}

function copyMessage(content: string) {
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(content).catch(() => {
      copyMessageWithFallback(content);
    });
    return;
  }
  copyMessageWithFallback(content);
}

function copyMessageWithFallback(content: string) {
  const textarea = document.createElement('textarea');
  textarea.value = content;
  textarea.setAttribute('readonly', 'true');
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  textarea.style.top = '0';
  document.body.appendChild(textarea);
  textarea.select();
  try {
    document.execCommand('copy');
  } finally {
    document.body.removeChild(textarea);
  }
}

function labelForRole(role: KnowledgeMessage['role'], userLabel: string) {
  if (role === 'assistant') {
    return 'R-DAP';
  }
  if (role === 'user') {
    return userLabel;
  }
  return 'System';
}

function labelFromUserRef(userRef: string | undefined) {
  if (!userRef || userRef === 'user:default/anonymous') {
    return 'User';
  }
  if (userRef.startsWith('user:')) {
    return userRef.split('/').pop() ?? userRef;
  }
  return userRef;
}

// The model call between tool invocations isn't observable (no reasoning
// stream), so this is a synthetic label inferred from the tool that just
// ran, not the model's actual reasoning text.
function thinkingLabelAfterTool(signal: AssistantSignal): string {
  if (signal.type === 'assistant.tool.failed') {
    return 'Reconsidering after a tool error';
  }

  const id = (signal.actionId ?? signal.toolName ?? '').toLowerCase();
  if (id.includes('techdocs') || id.includes('docs')) {
    return 'Reviewing documentation';
  }
  if (id.includes('catalog')) {
    return 'Reviewing catalog results';
  }
  if (id.includes('github')) {
    return 'Reviewing GitHub results';
  }
  if (id.includes('render-pdf')) {
    return 'Reviewing rendered page';
  }
  if (id.includes('save-image')) {
    return 'Confirming image saved';
  }
  if (id.includes('update-draft')) {
    return 'Reviewing proposed changes';
  }
  return 'Reviewing results';
}

function labelFromFocus(focus: KnowledgeSession['focus'] | undefined) {
  if (focus?.templateRef) {
    const [, templateRef] = focus.templateRef.split(/:(.*)/s);
    return `Template: ${templateRef ?? focus.templateRef}`;
  }
  const ref =
    focus?.entityRef ??
    focus?.componentRef ??
    focus?.systemRef ??
    focus?.domainRef ??
    focus?.tenantRef;
  if (ref) {
    const [kindPart, rest] = ref.includes(':')
      ? ref.split(/:(.*)/s)
      : [undefined, ref];
    const kind = focus?.kind ?? kindPart;
    const namespace = rest.includes('/')
      ? rest.split('/').slice(0, -1).join('/')
      : undefined;
    const name = rest.includes('/') ? rest.split('/').pop() ?? rest : rest;
    const nameWithNamespace = namespace ? `${namespace}/${name}` : name;
    return kind
      ? `${kind.charAt(0).toUpperCase()}${kind.slice(1)}: ${nameWithNamespace}`
      : nameWithNamespace;
  }
  return labelFromPagePath(focus?.pagePath) ?? focus?.pageTitle ?? 'General';
}

// Derived straight from the URL (always in sync with navigation) instead of
// document.title, which updates asynchronously and can briefly (or, if the
// page never calls setTitle, indefinitely) still hold the previous page's title.
function labelFromPagePath(pagePath: string | undefined) {
  if (!pagePath) {
    return undefined;
  }
  const [pathname, search] = pagePath.split('?');
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) {
    return 'Home';
  }
  const root = segments[0];
  if (root === 'create' && segments[1] === 'templates') {
    const [, , namespace, templateName] = segments;
    if (namespace && templateName) {
      return `Template: ${decodeURIComponent(namespace)}/${decodeURIComponent(
        templateName,
      )}`;
    }
    return 'Create templates';
  }
  if (root === 'catalog' && segments.length === 1) {
    const params = new URLSearchParams(search ?? '');
    const kind = params.get('filters[kind]');
    const owned = params.get('filters[user]') === 'owned';
    let label = 'Catalog';
    if (kind) {
      label += `: ${kind.charAt(0).toUpperCase()}${kind.slice(1)}`;
    }
    if (owned) {
      label += ' (Mine)';
    }
    return label;
  }
  return root.charAt(0).toUpperCase() + root.slice(1);
}

const markdownOptions = {
  disableParsingRawHTML: true,
  forceBlock: true,
  overrides: {
    a: { component: SafeLink },
    p: { component: ChatParagraph },
    h1: { component: ChatHeading },
    h2: { component: ChatHeading },
    h3: { component: ChatHeading },
    ul: { component: ChatList },
    ol: { component: ChatOrderedList },
    li: { component: ChatListItem },
    pre: { component: ChatPre },
    code: { component: ChatCode },
    table: { component: ChatTable },
    th: { component: ChatTableHeader },
    td: { component: ChatTableCell },
  },
};

function SafeLink(props: AnchorHTMLAttributes<HTMLAnchorElement>) {
  const colors = useKnowledgeBuilderColors();
  const href = isSafeHref(props.href) ? props.href : undefined;
  return (
    <a
      {...props}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: colors.accent.blue, overflowWrap: 'anywhere' }}
    />
  );
}

function isSafeHref(href: string | undefined) {
  if (!href) {
    return false;
  }
  return /^(https?:|mailto:|\/|#)/i.test(href);
}

function ChatParagraph(props: { children: ReactNode }) {
  const colors = useKnowledgeBuilderColors();
  return (
    <Typography
      component="p"
      style={{
        margin: '4px 0',
        fontSize: 12,
        lineHeight: 1.45,
        overflowWrap: 'anywhere',
        color: colors.text.primary,
      }}
    >
      {props.children}
    </Typography>
  );
}

function ChatHeading(props: { children: ReactNode }) {
  const colors = useKnowledgeBuilderColors();
  return (
    <Typography
      component="h3"
      variant="subtitle2"
      style={{
        margin: '6px 0 4px',
        fontSize: 12,
        fontWeight: 700,
        color: colors.text.primary,
      }}
    >
      {props.children}
    </Typography>
  );
}

function ChatList(props: { children: ReactNode }) {
  return <ul style={{ margin: '4px 0', paddingLeft: 18 }}>{props.children}</ul>;
}

function ChatOrderedList(props: { children: ReactNode }) {
  return <ol style={{ margin: '4px 0', paddingLeft: 18 }}>{props.children}</ol>;
}

function ChatListItem(props: { children: ReactNode }) {
  return <li style={{ margin: '2px 0' }}>{props.children}</li>;
}

function ChatPre(props: { children: ReactNode }) {
  const colors = useKnowledgeBuilderColors();
  const text = textFromChildren(props.children);
  const language = languageFromPreChildren(props.children);

  if (language === 'mermaid') {
    return (
      <div
        style={{
          margin: '8px 0',
          maxWidth: '100%',
          boxSizing: 'border-box',
          borderRadius: 8,
          background: colors.code.headerBg,
          border: `1px solid ${colors.code.border}`,
          overflow: 'hidden',
        }}
      >
        <MermaidDiagram code={text} />
      </div>
    );
  }

  return (
    <div
      style={{
        margin: '8px 0',
        maxWidth: '100%',
        boxSizing: 'border-box',
        borderRadius: 8,
        background: colors.code.headerBg,
        border: `1px solid ${colors.code.border}`,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          height: 30,
          padding: '0 8px 0 10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          color: colors.text.secondary,
          fontSize: 11,
          borderBottom: `1px solid ${colors.code.border}`,
          background: colors.code.headerBg,
        }}
      >
        <span>{language}</span>
        <Tooltip title="Copy code">
          <IconButton
            size="small"
            aria-label="Copy code"
            style={{ padding: 3, color: colors.text.secondary }}
            onClick={() => copyMessage(text)}
          >
            <FileCopyOutlinedIcon style={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </div>
      <pre
        style={{
          margin: 0,
          padding: '11px 12px 13px',
          overflowX: 'auto',
          maxWidth: '100%',
          boxSizing: 'border-box',
          background: colors.code.bg,
          color: colors.code.text,
          fontSize: 12,
          lineHeight: 1.55,
          whiteSpace: 'pre',
          tabSize: 2,
        }}
      >
        {props.children}
      </pre>
    </div>
  );
}

function ChatCode(props: { children: ReactNode; className?: string }) {
  const colors = useKnowledgeBuilderColors();
  const text = textFromChildren(props.children);
  const isBlock = text.includes('\n');
  if (isBlock) {
    return (
      <code
        style={{
          display: 'block',
          padding: 0,
          background: 'transparent',
          color: 'inherit',
          fontSize: 12,
          lineHeight: 1.55,
          whiteSpace: 'pre',
          tabSize: 2,
        }}
      >
        {props.children}
      </code>
    );
  }

  return (
    <code
      style={{
        padding: '1px 4px',
        borderRadius: 3,
        background: colors.code.inlineBg,
        color: colors.code.inlineText,
        fontSize: 12,
        whiteSpace: 'normal',
        overflowWrap: 'anywhere',
      }}
    >
      {props.children}
    </code>
  );
}

function textFromChildren(children: ReactNode): string {
  if (typeof children === 'string' || typeof children === 'number') {
    return String(children);
  }
  if (Array.isArray(children)) {
    return children.map(textFromChildren).join('');
  }
  if (isValidElement<{ children?: ReactNode }>(children)) {
    return textFromChildren(children.props.children);
  }
  return '';
}

function languageFromPreChildren(children: ReactNode) {
  const child = Array.isArray(children) ? children[0] : children;
  if (isValidElement<{ className?: string }>(child)) {
    const className = child.props.className ?? '';
    const match = className.match(/(?:language|lang)-([a-z0-9_+-]+)/i);
    if (match?.[1]) {
      return match[1].toLowerCase();
    }
  }
  return 'interpreter';
}

function ChatTable(props: { children: ReactNode }) {
  return (
    <table
      style={{
        width: '100%',
        maxWidth: '100%',
        margin: '6px 0',
        borderCollapse: 'collapse',
        fontSize: 12,
        tableLayout: 'fixed',
        overflowWrap: 'anywhere',
      }}
    >
      {props.children}
    </table>
  );
}

function ChatTableHeader(props: { children: ReactNode }) {
  const colors = useKnowledgeBuilderColors();
  return (
    <th
      style={{
        border: `1px solid ${colors.code.tableBorder}`,
        color: colors.text.primary,
        padding: '3px 5px',
        textAlign: 'left',
        verticalAlign: 'top',
      }}
    >
      {props.children}
    </th>
  );
}

function ChatTableCell(props: { children: ReactNode }) {
  const colors = useKnowledgeBuilderColors();
  return (
    <td
      style={{
        border: `1px solid ${colors.code.tableBorder}`,
        color: colors.text.primary,
        padding: '3px 5px',
        verticalAlign: 'top',
      }}
    >
      {props.children}
    </td>
  );
}

function AgentActivity(options: { activities: AssistantActivity[] }) {
  const colors = useKnowledgeBuilderColors();
  const activities = options.activities.length
    ? options.activities
    : [
        {
          key: 'fallback',
          label: 'Analyzing request',
          status: 'running' as const,
        },
      ];

  return (
    <div
      style={{
        marginTop: 5,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      {activities.map(activity => (
        <div
          key={activity.key}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            minHeight: 18,
            color:
              activity.status === 'failed'
                ? '#ef4444'
                : activity.status === 'running'
                ? colors.text.secondary
                : colors.text.muted,
            fontSize: 11,
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: 12,
              textAlign: 'center',
              fontWeight: 700,
            }}
          >
            {activity.status === 'completed'
              ? '✓'
              : activity.status === 'failed'
              ? '×'
              : '◉'}
          </span>
          <span>{activity.label}</span>
        </div>
      ))}
    </div>
  );
}

function sendButtonStyle(colors: KnowledgeBuilderColors) {
  return {
    width: 28,
    height: 28,
    padding: 4,
    borderRadius: '50%',
    color: colors.chat.sendButtonText,
    background: colors.chat.sendButtonBg,
  };
}

function attachButtonStyle(colors: KnowledgeBuilderColors) {
  return {
    width: 28,
    height: 28,
    padding: 4,
    borderRadius: '50%',
    color: colors.text.secondary,
    background: 'transparent',
  };
}

const sendRowStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

function suggestedPromptStyle(colors: KnowledgeBuilderColors) {
  return {
    textAlign: 'left' as const,
    fontSize: 12,
    lineHeight: 1.4,
    color: colors.text.secondary,
    background: colors.bg.panel,
    border: `1px solid ${colors.border.subtle}`,
    borderRadius: 8,
    padding: '8px 10px',
    cursor: 'pointer',
    width: 'auto',
    maxWidth: '75%',
  };
}
